import { z } from "zod";

import { type ContractDocument, contractDocumentSchema } from "./contractContent";

/**
 * SÖZLEŞME ŞABLONLARI (CLOS-124 `[106]`).
 *
 * ### ⚠ Şablon canlı sözleşme ÜRETMEZ
 *
 * Kart: *"oluşturma işlemi CANLI SÖZLEŞME KAYDI ÜRETMEZ."* Şablon bir kalıp; ondan
 * sözleşme türetmek başka bir kartın işi. Bu yüzden burada kapasite, yaşam döngüsü ya da
 * taraf kavramı yok.
 *
 * ### ⚠ İçerik YA metin YA dosya
 *
 * Kart: *"içerik VEYA desteklenen dosya."* Şema bunu XOR olarak zorluyor; veritabanında da
 * aynı kural `CHECK` ile duruyor — iki katman, tek gerçek.
 */

export const CONTRACT_TEMPLATE_STATUSES = ["active", "inactive"] as const;
export type ContractTemplateStatus = (typeof CONTRACT_TEMPLATE_STATUSES)[number];

export const CONTRACT_TEMPLATE_STATUS_NAMES: Record<ContractTemplateStatus, string> = {
  active: "Kullanımda",
  inactive: "Pasif",
};

const templateBaseSchema = z.object({
  name: z.string().trim().min(1, "Şablon adı zorunludur").max(160, "Ad en çok 160 karakter"),
  description: z.string().trim().max(1000, "Açıklama en çok 1000 karakter").nullish(),
  /** Katalogdaki tür kodu; boş bırakılırsa şablon her türde kullanılabilir. */
  contractType: z.string().trim().min(1).max(64).nullish(),
  status: z.enum(CONTRACT_TEMPLATE_STATUSES).default("active"),
  /** Uygulama içi metin şablonu (`[086]` belge modeli). */
  document: contractDocumentSchema.nullish(),
  /** Dosya tabanlı şablon; güvenli dosya kimliği. */
  storageObjectId: z.string().uuid("Geçerli bir dosya seçin").nullish(),
  /**
   * AD ÇAKIŞMASI ONAYI.
   *
   * ⚠ Kart *"aynı ada ilişkin çakışma UYARISI gösterilir"* diyor, "engellenir" demiyor:
   * hukuk ekipleri aynı adı bilerek kullanabiliyor (farklı müvekkil, farklı yıl). Bu yüzden
   * benzersizlik KISITI yok; ilk istek uyarıyla dönüyor, kullanıcı onaylarsa geçiyor.
   * Sessizce ikinci bir "Gizlilik Sözleşmesi" yaratmak, listede hangisinin hangisi olduğunu
   * kimsenin bilmediği bir kütüphane üretirdi.
   */
  acknowledgeDuplicateName: z.boolean().default(false),
});

/** ⚠ XOR: tam olarak biri dolu olmalı. */
const bodyRule = <T extends { document?: unknown; storageObjectId?: unknown }>(veri: T) =>
  Boolean(veri.document) !== Boolean(veri.storageObjectId);

export const createContractTemplateSchema = templateBaseSchema.refine(bodyRule, {
  message: "Şablon için metin ya da dosya verin; ikisini birden değil",
  path: ["document"],
});
export type CreateContractTemplateInput = z.infer<typeof createContractTemplateSchema>;

/**
 * EKRANIN doldurduğu alanlar (CLOS-124 `[106]`).
 *
 * ⚠ API şemasından AYRI: gövde (metin/dosya) formda bir alan değil, bir SEÇİM — kullanıcı
 * ya metin yazıyor ya dosya seçiyor ve ikisi aynı anda çizilmiyor. Form doğrulamasını
 * tam API şemasına bağlasaydık, henüz doldurulmamış gövde yüzünden form sessizce
 * gönderilmezdi; kullanıcı düğmeye basar ve hiçbir şey olmazdı.
 */
export const contractTemplateFormSchema = templateBaseSchema.pick({
  name: true,
  description: true,
  contractType: true,
});
export type ContractTemplateFormInput = z.infer<typeof contractTemplateFormSchema>;

/**
 * LİSTE SORGUSU (CLOS-125 `[107]`).
 *
 * ⚠ `status` verilmezse PASİF şablonlar listeye KARIŞMIYOR. Kart: *"arşiv şablonlar uygun
 * filtre olmadan karışmaz."* `[083]`'ün sözleşme listesindeki kararla aynı: kullanılmayan
 * bir kalıbı çalışan kütüphaneye karıştırmak, kullanıcıyı her seferinde ayıklamaya zorlar.
 * Pasifleri görmek isteyen `status=inactive` diyor — yani gizleme bir seçim değil,
 * varsayılan davranış.
 */
export const contractTemplateListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  q: z.string().trim().max(160).optional(),
  status: z.enum(CONTRACT_TEMPLATE_STATUSES).optional(),
  /** Tür süzgeci — katalog kodu (`config/contractMetadata.json`). */
  contractType: z.string().trim().min(1).max(64).optional(),
});
export type ContractTemplateListQuery = z.infer<typeof contractTemplateListQuerySchema>;

/**
 * Listede ve detayda gösterilen şablon.
 *
 * ⚠ Depolama adresi YOK (`[090]`/`[101]` kuralı): dosya tabanlı şablonun indirilmesi ayrı
 * ve süreli bir sözleşmenin işi.
 */
/**
 * DÜZENLEME GİRDİSİ (CLOS-126 `[108]`).
 *
 * ⚠ Alanların hepsi İSTEĞE BAĞLI: kullanıcı yalnız adı değiştirebilmeli ve bu, gövdeyi
 * yeniden göndermeyi gerektirmemeli. Zorunlu kılsaydık her düzenleme, dokunulmayan alanı
 * da yeniden yazar ve "kim neyi değiştirdi" sorusu bulanıklaşırdı.
 *
 * ⚠ Gövde değiştirilecekse yine XOR: metin ya da dosya, ikisi birden değil.
 */
export const updateContractTemplateSchema = z
  .object({
    name: templateBaseSchema.shape.name,
    description: templateBaseSchema.shape.description,
    contractType: templateBaseSchema.shape.contractType,
    /**
     * ⚠ Durum burada VARSAYILANSIZ. Oluşturma şemasındaki `.default("active")` düzenlemede
     * her isteğe sessizce bir `status` ekler ve "en az bir alan" kuralı hiçbir zaman
     * ısırmazdı — boş bir istek de geçerli sayılırdı. Testte tam olarak bu düştü.
     */
    /**
     * ⚠ DURUM BURADAN DEĞİŞMİYOR (CLOS-128 `[110]`). Arşivleme ve yeniden aktifleştirme
     * kendi uçlarında ve kendi yetkisinde (`template.archive`); düzenleme yetkisiyle
     * (`template.edit`) durum değiştirilebilseydi aynı işlemin iki kapısı olurdu ve
     * biri gevşek kalırdı. Alan şemada duruyor ama servis onu reddediyor.
     */
    status: z.enum(CONTRACT_TEMPLATE_STATUSES),
    document: templateBaseSchema.shape.document,
    storageObjectId: templateBaseSchema.shape.storageObjectId,
  })
  .partial()
  .refine((veri) => !(veri.document && veri.storageObjectId), {
    message: "Şablon için metin ya da dosya verin; ikisini birden değil",
    path: ["document"],
  })
  .refine((veri) => Object.values(veri).some((d) => d !== undefined), {
    message: "Değiştirilecek en az bir alan gerekiyor",
    path: ["name"],
  });
export type UpdateContractTemplateInput = z.infer<typeof updateContractTemplateSchema>;

/**
 * ÇOĞALTMA GİRDİSİ (CLOS-127 `[109]`).
 *
 * ⚠ Yeni ad ZORUNLU. Kartın ilk kriteri: kopyaya "X (kopya)" gibi bir ad uydursaydık,
 * kütüphanede kimin ne için açtığı belirsiz satırlar birikirdi. Adı kullanıcı veriyor.
 */
export const copyContractTemplateSchema = z.object({
  name: templateBaseSchema.shape.name,
  /** Ad çakışması yine UYARI (`[106]` kuralı); onay aynı biçimde taşınıyor. */
  acknowledgeDuplicateName: z.boolean().default(false),
});
export type CopyContractTemplateInput = z.infer<typeof copyContractTemplateSchema>;

/** Şablonun bir önceki hâli — düzenleme geçmişi. */
export type ContractTemplateVersionView = {
  id: string;
  versionNumber: number;
  name: string;
  status: ContractTemplateStatus;
  bodyKind: "document" | "file";
  clauseCount: number | null;
  fileName: string | null;
  changedByName: string;
  createdAt: string;
};

/**
 * ARŞİVLEME (CLOS-128 `[110]`).
 *
 * ⚠ Arşiv, `inactive` durumunun kendisi: `[107]`'de liste zaten pasifleri varsayılan
 * görünümden çıkarıyor. Üçüncü bir "archived" durumu eklemek, aynı anlamı iki isimle
 * taşımak olurdu.
 */
export const archiveContractTemplateSchema = z.object({
  reason: z.string().trim().max(500, "Gerekçe en fazla 500 karakter olabilir").nullish(),
});
export type ArchiveContractTemplateInput = z.infer<typeof archiveContractTemplateSchema>;

export const CONTRACT_TEMPLATE_STATUS_CHANGE_MESSAGE =
  "Şablon durumu düzenleme ekranından değiştirilemez; arşivleme ve yeniden aktifleştirme ayrı yetkiye tabidir.";

export const CONTRACT_TEMPLATE_ARCHIVE_EFFECTS = [
  "Şablon yeni sözleşmelerde seçilemez.",
  "Bu şablondan üretilmiş mevcut sözleşmeler etkilenmez.",
  "Geçmiş, düzenleme sürümleri ve audit kayıtları korunur.",
  "Yeniden aktifleştirme ayrı bir yetki gerektirir.",
] as const;

/** Sistem şablonu düzenlenemez — mesaj şemada, ekran cümle uydurmuyor. */
export const CONTRACT_TEMPLATE_SYSTEM_LOCKED_MESSAGE =
  "Sistem şablonları düzenlenemez. Kendi kopyanızı oluşturup düzenleyebilirsiniz.";

export type ContractTemplateView = {
  id: string;
  name: string;
  description: string | null;
  contractType: string | null;
  status: ContractTemplateStatus;
  /** Şablonun gövdesi metin mi dosya mı — ekran buna göre davranıyor. */
  bodyKind: "document" | "file";
  fileName: string | null;
  contentType: string | null;
  clauseCount: number | null;
  /** Ürünle gelen şablon mu — düzenlemeye kapalı. */
  isSystem: boolean;
  createdByName: string;
  createdAt: string;
  /** Kaç kez düzenlendi (sürüm geçmişi uzunluğu). */
  revisionCount: number;
  /**
   * Şablonun METNİ — yalnız DETAY ucunda dolu (CLOS-126).
   *
   * ⚠ Listede `null`: kütüphanede yirmi şablonun tam metnini taşımak, kimsenin okumadığı
   * bir yükü her sayfada göndermek olurdu. Detayda dolu çünkü düzenleme ekranı mevcut
   * metni GÖSTERMEK zorunda — göstermeden düzenleme, kullanıcıya "her şeyi baştan yaz"
   * demektir.
   */
  document: ContractDocument | null;
};

/** Ad çakışması yanıtı — istemci uyarıyı gösterip onayla tekrar gönderiyor. */
export const CONTRACT_TEMPLATE_DUPLICATE_NAME_CODE = "duplicate_name";
export const CONTRACT_TEMPLATE_DUPLICATE_NAME_MESSAGE =
  "Bu adda bir şablon zaten var. Yine de oluşturmak istiyor musunuz?";

/**
 * DOLDURULMUŞ METİN ÖN İZLEMESİ (CLOS-132 `[113]`).
 *
 * ⚠ Başlık burada da isteniyor: `sozlesme.baslik` sistem değişkeni ondan doluyor ve ön
 * izlemede farklı bir başlık gösterip sözleşmeye başkasını yazmak, onaya sunulan metnin
 * yalan olması demekti.
 */
export const previewContractTemplateSchema = z.object({
  title: z.string().trim().min(1, "Sözleşme başlığı zorunludur").max(200),
  variables: z.record(z.string(), z.string().max(500)).optional(),
});
export type PreviewContractTemplateInput = z.infer<typeof previewContractTemplateSchema>;
